import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import {
  PublishedWithChangesOutlined,
  FormatListBulletedOutlined,
  AutorenewOutlined,
  CancelOutlined,
  TaskAlt,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material'

export default function ClaimsBulkSidebarLayout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const isRtl = i18n.language === 'ar'
  const [collapsed, setCollapsed] = useState(() => {
    const saved = sessionStorage.getItem('claimsBulkSidebarCollapsed')
    return saved === null ? true : JSON.parse(saved)
  })

  const currentPath = location.pathname

  useEffect(() => {
    sessionStorage.setItem(
      'claimsBulkSidebarCollapsed',
      JSON.stringify(collapsed)
    )
  }, [collapsed])

  const menuItems = [
    {
      id: 'diagnosis',
      path: '/claims/diagnosis-bulk-replace',
      label: t('ICD Code Replace'),
      icon: <PublishedWithChangesOutlined className="!w-4 !h-4 shrink-0" />,
      active: currentPath.includes('diagnosis-bulk-replace')
    },
    {
      id: 'item',
      path: '/claims/item-bulk-replace',
      label: t('Item Field Replace'),
      icon: <FormatListBulletedOutlined className="!w-4 !h-4 shrink-0" />,
      active: currentPath.includes('item-bulk-replace')
    },
    {
      id: 'referral',
      path: '/claims/referral-bulk-update',
      label: t('Referral Update'),
      icon: <AutorenewOutlined className="!w-4 !h-4 shrink-0" />,
      active: currentPath.includes('referral-bulk-update')
    },
    {
      id: 'bulk-cancel',
      path: '/claims/bulk-cancel',
      label: t('Bulk Cancel'),
      icon: <CancelOutlined className="!w-4 !h-4 shrink-0" />,
      active: currentPath.includes('bulk-cancel')
    },
    {
      id: 'bulk-ready',
      path: '/claims/bulk-ready',
      label: t('Bulk Ready'),
      icon: <TaskAlt className="!w-4 !h-4 shrink-0" />,
      active: currentPath.includes('bulk-ready')
    }
  ]

  return (
    <div className="flex gap-2.5 w-full h-[calc(100vh-60px)] items-start overflow-hidden">
      <div
        className={`bg-white border-r border-b border-gray-200 py-2.5 transition-all duration-300 flex flex-col shrink-0 h-full shadow-xs ${
          collapsed ? 'w-12 px-1.5' : 'w-48 px-2'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 pb-2.5 mb-2.5 min-h-[32px]">
          {!collapsed ? (
            <h2 className="text-[13px] font-bold text-gray-800 uppercase tracking-wider truncate px-1">
              {t('Bulk Operations')}
            </h2>
          ) : null}
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className={`p-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors shrink-0 ${
              collapsed ? 'mx-auto' : ''
            }`}
            title={collapsed ? t('Expand Sidebar') : t('Collapse Sidebar')}
          >
            {collapsed ? (
              isRtl ? <ChevronLeft className="!w-4.5 !h-4.5" /> : <ChevronRight className="!w-4.5 !h-4.5" />
            ) : (
              isRtl ? <ChevronRight className="!w-4.5 !h-4.5" /> : <ChevronLeft className="!w-4.5 !h-4.5" />
            )}
          </button>
        </div>

        {/* Sidebar Nav Items */}
        <nav className="space-y-1 flex-1 overflow-y-auto">
          {menuItems.map(item => {
            const buttonContent = (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-2 rounded-md transition-all ${
                  collapsed ? 'justify-center p-2' : 'px-2.5 py-1.5 text-sm font-semibold'
                } ${
                  item.active
                    ? 'bg-[#0070C2]/10 text-[#0070C2]'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                {item.icon}
                {!collapsed && <span className="truncate">{item.label}</span>}
              </button>
            )

            if (collapsed) {
              return (
                <Tooltip
                  key={item.id}
                  title={item.label}
                  placement={isRtl ? 'left' : 'right'}
                  arrow
                >
                  <div>{buttonContent}</div>
                </Tooltip>
              )
            }

            return buttonContent
          })}
        </nav>
      </div>
      <div className="flex-1 min-w-0 w-full h-full overflow-y-auto pt-2 pb-6 pr-2">
        {children}
      </div>
    </div>
  )
}
